import { lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import Evidence from "../../container/Evidence/Evidence";
import logger from "../../utils/logger";
import useSEO from "../../utils/useSEO";

import CinematicNav from "./sections/CinematicNav";
import CinematicHero from "./sections/CinematicHero";
import CinematicFeatures from "./sections/CinematicFeatures";

import CinematicProtocol from "./sections/CinematicProtocol";
import CinematicMobileApp from "./sections/CinematicMobileApp";
import CinematicFooter from "./sections/CinematicFooter";

import "./cinematic.css";

// Stripe-heavy and below the fold — load on demand.
const PricingPage = lazy(() => import("../Pricing"));

// SVG noise texture overlay for cinematic grain effect
const NoiseOverlay = () => (
  <svg className="cinematic-noise" aria-hidden="true">
    <filter id="cinematic-noise-filter">
      <feTurbulence
        type="fractalNoise"
        baseFrequency="0.65"
        numOctaves="3"
        stitchTiles="stitch"
      />
    </filter>
    <rect
      width="100%"
      height="100%"
      filter="url(#cinematic-noise-filter)"
    />
  </svg>
);

function Presentation() {
  const location = useLocation();

  useSEO({
    title: "JobHarvest | Land Your Dream Job on Autopilot",
    // "the most advanced auto-apply platform" was an unsubstantiated
    // superiority claim over named competitors we have no comparison data for.
    description:
      "JobHarvest applies to jobs for you. Set your preferences once, swipe on the roles you want, and our AI fills in and submits each employer's application on your behalf.",
    canonicalPath: "/",
  });

  // Arriving with a hash (e.g. "/#pricing" from the mobile-app page's nav):
  // land at the top of the page first, then animate the scroll down to the
  // section once the page has painted — instead of an instant anchor jump.
  useEffect(() => {
    if (!location.hash) return undefined;
    const target = document.getElementById(location.hash.slice(1));
    if (!target) return undefined;

    window.scrollTo({ top: 0, behavior: "auto" });
    const timer = setTimeout(() => {
      target.scrollIntoView({ behavior: "smooth" });
    }, 150);
    return () => clearTimeout(timer);
  }, [location.hash]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referrerId = params.get("invite");
    const referrerSource = document.referrer || "direct";

    if (referrerId) {
      logger.log("saving referrerId:", referrerId);
      logger.log("saving referrerSource:", referrerSource);
      localStorage.setItem("referrerId", referrerId);
      localStorage.setItem("referralSource", referrerSource);
    }
  }, [location.search]);

  return (
    <>
      <NoiseOverlay />
      <CinematicNav />

      <CinematicHero />

      <Box
        component="main"
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <CinematicFeatures />

        <Evidence />

        <CinematicProtocol />

        <Box
          id="pricing"
          className="cinematic-section-dark"
        >
          <Suspense fallback={null}>
            <PricingPage isDemo={true} embedded={true} />
          </Suspense>
        </Box>

        <CinematicMobileApp />

        <CinematicFooter />
      </Box>
    </>
  );
}

export default Presentation;
